import { z } from "zod/v3";
import { kernelQuery, kernelMutation } from "../client.js";
import { success, successResult, failureResult } from "../envelope.js";

export const labelsSchema = z.object({
  action: z.enum(["list", "create", "update", "delete", "apply", "remove"])
    .describe("Action: list all labels, create/update/delete a label, or apply/remove a label on an entry"),
  slug: z.string().max(200).optional().describe("Label slug (required for create/update/delete/apply/remove)"),
  name: z.string().max(500).optional().describe("Display name (required for create)"),
  color: z.string().max(50).optional().describe("Hex color, e.g. '#ef4444'"),
  description: z.string().max(2000).optional().describe("What this label means"),
  parentSlug: z.string().max(200).optional().describe("Parent group slug for label hierarchy"),
  isGroup: z.boolean().optional().describe("True if this is a group container, not a taggable label"),
  order: z.number().optional().describe("Sort order within its group"),
  entryId: z.string().max(200).optional().describe("Entry ID for apply/remove actions"),
});

/**
 * WP-484 S2 (§3): extracted label handlers — reused by the legacy `labels`
 * standalone tool AND `collections action=label-*` (absorbed per §3
 * core-nouns table). One business-logic path per action.
 */
export async function handleLabelsList() {
  const labels = await kernelQuery<any[]>("chain.listLabels");

  if (labels.length === 0) {
    return successResult(
      "No labels defined in this workspace yet.",
      "No labels defined yet.",
      { labels: [], total: 0 },
      [{ tool: "collections", description: "Create a label", parameters: { action: "label-create" } }],
    );
  }

  const groups = labels.filter((l) => l.isGroup);
  const ungrouped = labels.filter((l) => !l.isGroup && !l.parentId);
  const children = (parentId: string) => labels.filter((l) => l.parentId === parentId);

  const lines: string[] = ["# Workspace Labels"];

  for (const group of groups) {
    lines.push(`\n## ${group.name}`);
    if (group.description) lines.push(`_${group.description}_`);
    for (const child of children(group._id)) {
      const c = child.color ? ` ${child.color}` : "";
      lines.push(`  - \`${child.slug}\` ${child.name}${c}`);
    }
  }

  if (ungrouped.length > 0) {
    lines.push("\n## Ungrouped");
    for (const label of ungrouped) {
      const c = label.color ? ` ${label.color}` : "";
      lines.push(`- \`${label.slug}\` ${label.name}${c}${label.description ? ` — _${label.description}_` : ""}`);
    }
  }

  return {
    content: [{ type: "text" as const, text: lines.join("\n") }],
    structuredContent: success(
      `Found ${labels.length} labels in ${groups.length} groups.`,
      { labels, total: labels.length },
    ),
  };
}

export async function handleLabelsCreate(
  slug: string,
  name: string,
  color?: string,
  description?: string,
  parentSlug?: string,
  isGroup?: boolean,
  order?: number,
) {
  let parentId: string | undefined;
  if (parentSlug) {
    const labels = await kernelQuery<any[]>("chain.listLabels");
    const parent = labels.find((l: any) => l.slug === parentSlug);
    if (!parent) {
      return failureResult(
        `Parent label '${parentSlug}' not found. Use collections action=label-list to see available groups.`,
        "NOT_FOUND",
        `Parent label '${parentSlug}' not found.`,
        "Use collections action=label-list to see available groups.",
        [{ tool: "collections", description: "List labels", parameters: { action: "label-list" } }],
      );
    }
    parentId = parent._id;
  }

  await kernelMutation("chain.createLabel", { slug, name, color, description, parentId, isGroup, order });
  return successResult(
    `Label '${name}' (${slug}) created.`,
    `Created label '${name}' (${slug}).`,
    { slug, name, color, parentSlug },
    [{ tool: "collections", description: "List labels", parameters: { action: "label-list" } }],
  );
}

export async function handleLabelsUpdate(
  slug: string,
  name?: string,
  color?: string,
  description?: string,
  isGroup?: boolean,
  order?: number,
) {
  await kernelMutation("chain.updateLabel", { slug, name, color, description, isGroup, order });
  return successResult(
    `Label '${slug}' updated.`,
    `Updated label '${slug}'.`,
    { slug },
    [{ tool: "collections", description: "List labels", parameters: { action: "label-list" } }],
  );
}

export async function handleLabelsDelete(slug: string) {
  await kernelMutation("chain.deleteLabel", { slug });
  return successResult(
    `Label '${slug}' removed from all entries and deleted.`,
    `Deleted label '${slug}'.`,
    { slug },
  );
}

export async function handleLabelsApply(slug: string, entryId: string) {
  await kernelMutation("chain.applyLabel", { entryId, labelSlug: slug });
  return successResult(`Label '${slug}' applied to ${entryId}.`, `Applied label '${slug}' to ${entryId}.`, { slug, entryId });
}

export async function handleLabelsRemove(slug: string, entryId: string) {
  await kernelMutation("chain.removeLabel", { entryId, labelSlug: slug });
  return successResult(`Label '${slug}' removed from ${entryId}.`, `Removed label '${slug}' from ${entryId}.`, { slug, entryId });
}

// WP-484 S3 (clean break, TEN-2157): the standalone `labels` tool retired
// here. handleLabels* stay live — collections.ts (the `collections` compound,
// action=label-*) is their only remaining caller.
